import { useEffect, useMemo, useState } from 'react'

import {
  usePromotionKarteGetApi,
  usePromotionKarteHistoryApi,
  usePromotionKarteUpdateApi
} from '@/common/api/modules/promotionKarte'
import { PromotionKarteConfigItem } from '@/common/api/params/promotionKarte'
import { useShowSavedToast } from '@/common/app/toast'
import { dateTimeStr } from '@/common/form/date'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageTitle from '@/components/view/molecules/title/PageTitle'

// ============================================================
// 本画面の設定は DataInsight（プロモーションカルテ）が読み取って実行方式を切り替えるための値であり、
// 管理者が現在の連携状況を把握・管理するための画面。実装スコープは 2 セクション：
//   ①「施策別 連携方式」（API準備状況を含む） ②「W2 管理画面 登録URL」＋ 変更履歴。
// 保存先は正規化テーブル promotion_karte_configs。共有APIの提供状況は予約キー行で保持する。
// ============================================================

// 共有API（告知/ターゲットリスト）の提供状況を保持する予約キー
const NOTIFY_API = '__api_notify__'
const TARGETLIST_API = '__api_targetlist__'

// API準備状況の選択肢と、接続済み判定
const API_STATUS_OPTIONS = ['未提供', '提供済み', '接続済み']
const EXTERNAL_STATUS = '対象外（外部媒体）'
const CONNECTED = '接続済み'

// 連携方式セレクトの選択肢（disabled=API未接続でグレーアウト）
interface Opt {
  value: string
  disabled?: boolean
}

// 各列の非API選択肢＋API連携の有無（API連携はステータス接続済みで有効化）
interface ColCfg {
  base: string[]
  api: boolean
}

interface PromoDef {
  code: string
  name: string
  icon: string
  iconColor: string
  mode?: {
    w2Table: string[]
    external?: boolean // 対象外（外部媒体）＝API準備状況は固定・API連携なし
    integration: ColCfg
    notify: ColCfg
    targetlist: ColCfg
    enabledDefault: boolean
  }
  url?: {
    w2Feature: string
    pathDefault: string
    pathPlaceholder?: string
    // 2026-10-04 追加（SMART_HUB_DEV-152）：V6 の登録URLの入力例。V5 に該当機能が無い施策は noV5
    pathPlaceholderV6?: string
    noV5?: boolean
    param: string[]
  }
}

// API連携オプションのラベル
const AP_INTEGRATION = 'API連携'
const AP_NOTIFY = 'API連携'
const AP_TARGETLIST = 'API連携（プール方式）'

const PROMOS: PromoDef[] = [
  {
    code: 'coupon',
    name: 'クーポン',
    icon: '🎟️',
    iconColor: '#D08A1A',
    mode: {
      w2Table: ['w2_Coupon'],
      integration: { base: ['管理画面誘導', '手動'], api: true },
      notify: { base: ['管理画面誘導'], api: true },
      targetlist: { base: ['管理画面誘導', '不要'], api: true },
      enabledDefault: true
    },
    url: {
      w2Feature: 'クーポン管理 > 新規登録',
      pathDefault: '/coupon/edit',
      param: ['あり（クエリ）', 'なし']
    }
  },
  {
    code: 'point',
    name: 'ポイント',
    icon: '⭐',
    iconColor: '#7A5BD6',
    mode: {
      w2Table: ['w2_PointRule', 'PointRuleSchedule'],
      integration: { base: ['管理画面誘導', '手動'], api: true },
      notify: { base: ['管理画面誘導'], api: true },
      targetlist: { base: ['不要'], api: true },
      enabledDefault: true
    },
    url: {
      w2Feature: 'ポイントルール管理',
      pathDefault: '/point/rule',
      param: ['なし', 'あり（クエリ）']
    }
  },
  {
    code: 'campaign',
    name: 'キャンペーン',
    icon: '🎯',
    iconColor: '#D08A1A',
    mode: {
      w2Table: ['w2_FeaturePage 系'],
      integration: { base: ['管理画面誘導', '手動'], api: true },
      notify: { base: ['管理画面誘導'], api: false },
      targetlist: { base: ['不要'], api: false },
      enabledDefault: true
    },
    url: {
      w2Feature: '特集ページ管理',
      pathDefault: '/feature/page',
      param: ['なし', 'あり（クエリ）']
    }
  },
  {
    code: 'crm',
    name: 'CRM',
    icon: '✉️',
    iconColor: '#0C6E6A',
    mode: {
      w2Table: ['w2_MailDistSetting'],
      integration: { base: ['管理画面誘導'], api: true },
      notify: { base: ['不要（配信自体が告知）'], api: false },
      targetlist: { base: ['不要'], api: true },
      enabledDefault: true
    },
    url: {
      w2Feature: 'メール配信設定',
      pathDefault: '/maildist/setting',
      pathPlaceholderV6: '/_w2mpManager/mail-dist-text/list',
      param: ['あり（クエリ）', 'なし']
    }
  },
  {
    code: 'ad',
    name: '広告',
    icon: '📣',
    iconColor: '#0C6E6A',
    mode: {
      w2Table: ['（W2対象外）'],
      external: true,
      integration: { base: ['手動', '管理画面誘導'], api: false },
      notify: { base: ['不要'], api: false },
      targetlist: { base: ['不要'], api: false },
      enabledDefault: false
    }
  },
  {
    code: 'bundle',
    name: '同梱／セット',
    icon: '📦',
    iconColor: '#0C6E6A',
    mode: {
      w2Table: ['w2_SetPromotion', 'SetPromotionItem'],
      integration: { base: ['管理画面誘導'], api: true },
      notify: { base: ['管理画面誘導'], api: false },
      targetlist: { base: ['不要'], api: false },
      enabledDefault: true
    },
    url: {
      w2Feature: 'セットプロモーション管理',
      pathDefault: '/setpromotion',
      pathPlaceholderV6: '/_w2cManager/set-promotion/list',
      param: ['なし', 'あり（クエリ）']
    }
  },
  {
    code: 'novelty',
    name: 'ノベルティ',
    icon: '🎁',
    iconColor: '#7A5BD6',
    mode: {
      w2Table: ['w2_Novelty 系'],
      integration: { base: ['管理画面誘導'], api: true },
      notify: { base: ['不要', '管理画面誘導'], api: false },
      targetlist: { base: ['不要'], api: false },
      enabledDefault: true
    },
    url: {
      w2Feature: 'ノベルティ設定',
      pathDefault: '',
      pathPlaceholder: '/xxxx（未登録）',
      param: ['なし', 'あり（クエリ）']
    }
  },
  {
    code: 'subscribe',
    // 2026-10-06 変更（SMART_HUB_DEV-164）：名称を「定期会員」→「定期購入会員割引」。
    //   会員単位の定期会員割引＝会員ランク設定の「定期会員割引率」（w2_MemberRank.fixed_purchase_discount_rate）。
    //   商品単位の定期購入割引（discount）とは W2 の受注でも別の列に記録される
    //   変更前：w2Table ['要確認']、w2Feature '定期コース設定'、pathPlaceholder '/xxxx（未登録）'
    name: '定期購入会員割引',
    icon: '🔁',
    iconColor: '#1A9E6B',
    mode: {
      w2Table: ['w2_MemberRank'],
      integration: { base: ['管理画面誘導'], api: true },
      notify: { base: ['管理画面誘導'], api: false },
      targetlist: { base: ['不要'], api: false },
      enabledDefault: true
    },
    url: {
      w2Feature: '会員ランク設定 > 定期会員割引率',
      pathDefault: '',
      pathPlaceholder: '/_w2mpManager/Form/MemberRank/MemberRankList.aspx',
      pathPlaceholderV6: '/_w2mpManager/member-rank/list',
      param: ['なし', 'あり（クエリ）']
    }
  },
  {
    code: 'discount',
    // 2026-10-06 変更（SMART_HUB_DEV-164）：名称を「定期値引き」→「定期購入商品割引」（商品単位の定期購入割引）
    name: '定期購入商品割引',
    icon: '🏷️',
    iconColor: '#D24B4B',
    mode: {
      // 2026-10-04 変更（SMART_HUB_DEV-152）：定期の値引きは商品情報の「定期購入割引設定」が該当する
      //   （セール設定は「定期のみ」商品に適用されない）。対象テーブル名は EC 側に確認中
      w2Table: ['要確認（定期購入割引設定）'],
      integration: { base: ['管理画面誘導'], api: true },
      notify: { base: ['管理画面誘導'], api: true },
      targetlist: { base: ['不要'], api: true },
      enabledDefault: true
    },
    url: {
      w2Feature: '商品情報 > 定期購入割引設定',
      pathDefault: '',
      pathPlaceholder: '/xxxx（未登録）',
      param: ['あり（クエリ）', 'なし']
    }
  },
  {
    // 2026-10-07 追加（SMART_HUB_DEV-164）：通常のセール（タイムセール／シークレットセール）。
    //   「定期値引き」が実質的に指していた商品セール設定を、定期購入商品割引と分けて独立した施策にする
    //   （DEV-152 以前の discount の定義を引き継ぐ）
    code: 'sale',
    name: 'セール',
    icon: '🛍️',
    iconColor: '#D24B4B',
    mode: {
      w2Table: ['w2_ProductSale', 'ProductSalePrice'],
      integration: { base: ['管理画面誘導'], api: true },
      notify: { base: ['管理画面誘導'], api: true },
      targetlist: { base: ['不要'], api: true },
      enabledDefault: true
    },
    url: {
      w2Feature: 'プロモーション管理 > セール設定（タイムセール／シークレットセール）',
      pathDefault: '/productsale',
      param: ['あり（クエリ）', 'なし']
    }
  },
  {
    code: 'rank',
    name: '会員ランク',
    icon: '🏅',
    iconColor: '#7A5BD6',
    mode: {
      w2Table: ['w2_MemberRankRule'],
      integration: { base: ['管理画面誘導'], api: true },
      notify: { base: ['不要'], api: false },
      targetlist: { base: ['不要'], api: false },
      enabledDefault: false
    },
    url: {
      w2Feature: '会員ランク付与ルール',
      pathDefault: '/memberrank/rule',
      pathPlaceholderV6: '/_w2mpManager/member-rank-rule/list',
      param: ['なし', 'あり（クエリ）']
    }
  },
  {
    code: 'shipping',
    name: '送料',
    icon: '🚚',
    iconColor: '#0C6E6A',
    mode: {
      w2Table: ['w2_ShopShipping 系'],
      integration: { base: ['管理画面誘導'], api: true },
      notify: { base: ['管理画面誘導'], api: false },
      targetlist: { base: ['不要'], api: false },
      enabledDefault: true
    },
    url: {
      w2Feature: '配送種別・送料設定',
      pathDefault: '/shipping',
      param: ['なし', 'あり（クエリ）']
    }
  },
  {
    code: 'popup',
    name: 'ポップアップ（告知）',
    icon: '📣',
    iconColor: '#6366F1',
    // 2026-10-04 変更（SMART_HUB_DEV-152）：V6.17.0 で追加された CMS＞ポップアップビルダー（オプション）。V5 には該当機能なし
    url: {
      w2Feature: 'CMS > ポップアップビルダー（V6.17.0〜・オプション）',
      pathDefault: '',
      pathPlaceholderV6: '/_w2cmsManager/Popup/Main',
      noV5: true,
      param: ['なし']
    }
  }
]

// ベースURL（プレビュー表示用トークン）。実際の利用時に各テナントのサイトURLへ解決される。
const SITE_URL_TOKEN = '{site_url}'

// 施策コード→名称、履歴フィールド→ラベル
const nameOf = (code: string): string => {
  if (code === NOTIFY_API) return '告知（ポップアップ）API'
  if (code === TARGETLIST_API) return 'ターゲットリスト API'
  return PROMOS.find((p) => p.code === code)?.name ?? code
}
const FIELD_LABEL: Record<string, string> = {
  api_status: 'API準備状況',
  integration_mode: '施策登録の連携方式',
  notify_mode: '告知（ポップアップ）',
  target_list_mode: 'ターゲットリスト',
  enabled: '有効',
  admin_path: '登録URL（V5）',
  admin_path_v6: '登録URL（V6）',
  param_carry: 'パラメータ引継ぎ'
}
const dispVal = (field: string, v: string | null): string => {
  if (field === 'enabled') return v === 'true' ? '有効' : '無効'
  return v && v.length > 0 ? v : '（未設定）'
}

// 1施策ぶんの編集状態
interface RowState {
  api_status: string
  integration_mode: string
  notify_mode: string
  target_list_mode: string
  enabled: boolean
  admin_path: string
  admin_path_v6: string
  param_carry: string
}
type State = Record<string, RowState>

const defaultRow = (p: PromoDef): RowState => ({
  api_status: p.mode ? (p.mode.external ? EXTERNAL_STATUS : '未提供') : '',
  integration_mode: p.mode ? p.mode.integration.base[0] : '',
  notify_mode: p.mode ? p.mode.notify.base[0] : '',
  target_list_mode: p.mode ? p.mode.targetlist.base[0] : '',
  enabled: p.mode ? p.mode.enabledDefault : false,
  admin_path: p.url ? p.url.pathDefault : '',
  admin_path_v6: '',
  param_carry: p.url ? p.url.param[0] : ''
})
const emptyRow = (apiStatus: string): RowState => ({
  api_status: apiStatus,
  integration_mode: '',
  notify_mode: '',
  target_list_mode: '',
  enabled: false,
  admin_path: '',
  admin_path_v6: '',
  param_carry: ''
})

// 連携方式セレクトの配色（API=緑 / 管理画面=橙 / 手動・不要=グレー）
const modeStyle = (v: string): { bg: string; border: string; color: string } => {
  if (v.startsWith('API連携')) return { bg: '#E2F0EF', border: '#BFE0DD', color: '#0C6E6A' }
  if (v.startsWith('管理画面')) return { bg: '#FBF1DE', border: '#EDD9AE', color: '#8A5A0A' }
  return { bg: '#EEF1F4', border: '#E2E6EB', color: '#5A636E' }
}
// API準備状況の配色
const apiStatusStyle = (v: string): { bg: string; color: string } => {
  if (v === CONNECTED) return { bg: '#E5F4ED', color: '#1A9E6B' }
  if (v === '提供済み') return { bg: '#FBF1DE', color: '#D08A1A' }
  if (v === EXTERNAL_STATUS) return { bg: '#EEF1F4', color: '#5A636E' }
  return { bg: '#FBE9E9', color: '#D24B4B' } // 未提供
}

// 列設定＋接続状態から選択肢を作る（API連携は接続済みのみ有効）
const buildOpts = (cfg: ColCfg, apiLabel: string, apiConnected: boolean): Opt[] => {
  const opts: Opt[] = cfg.base.map((v) => ({ value: v }))
  if (cfg.api) opts.push({ value: apiLabel, disabled: !apiConnected })
  return opts
}

const th =
  'border-primary border px-3 py-2 text-left align-middle text-xs font-normal whitespace-nowrap'
const td = 'border-primary border px-3 py-2 align-middle'

const ModeSelect = ({
  options,
  value,
  onChange
}: {
  options: Opt[]
  value: string
  onChange: (v: string) => void
}) => {
  const s = modeStyle(value)
  return (
    <select
      className='rounded-md border px-2.5 py-1.5 text-xs font-semibold'
      style={{ background: s.bg, borderColor: s.border, color: s.color, minWidth: 132 }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {options.map((o) => (
        <option key={o.value} value={o.value} disabled={o.disabled}>
          {o.value}
        </option>
      ))}
    </select>
  )
}

// API準備状況セレクト（未提供/提供済み/接続済み）
const ApiStatusSelect = ({ value, onChange }: { value: string; onChange: (v: string) => void }) => {
  const s = apiStatusStyle(value)
  return (
    <select
      className='rounded-md border px-2 py-1 text-[11px] font-bold'
      style={{ background: s.bg, borderColor: s.bg, color: s.color, minWidth: 96 }}
      value={value}
      onChange={(e) => onChange(e.target.value)}
    >
      {API_STATUS_OPTIONS.map((o) => (
        <option key={o} value={o}>
          {o}
        </option>
      ))}
    </select>
  )
}

const PromotionKarteConfigPage = () => {
  const { data, mutate } = usePromotionKarteGetApi()
  const { data: historyData, mutate: mutateHistory } = usePromotionKarteHistoryApi()

  const defaults = useMemo<State>(() => {
    const d: State = {}
    for (const p of PROMOS) d[p.code] = defaultRow(p)
    d[NOTIFY_API] = emptyRow('未提供')
    d[TARGETLIST_API] = emptyRow('未提供')
    return d
  }, [])

  const [state, setState] = useState<State>(defaults)
  const [saved, setSaved] = useState<State>(defaults)

  useEffect(() => {
    if (!data) return
    const byType = new Map(data.items.map((it) => [it.promotion_type, it]))
    const built: State = {}
    const codes = [...PROMOS.map((p) => p.code), NOTIFY_API, TARGETLIST_API]
    for (const code of codes) {
      const base = defaults[code]
      const row = byType.get(code)
      built[code] = row
        ? {
            api_status: row.api_status ?? base.api_status,
            integration_mode: row.integration_mode ?? base.integration_mode,
            notify_mode: row.notify_mode ?? base.notify_mode,
            target_list_mode: row.target_list_mode ?? base.target_list_mode,
            enabled: row.enabled,
            admin_path: row.admin_path ?? base.admin_path,
            admin_path_v6: row.admin_path_v6 ?? base.admin_path_v6,
            param_carry: row.param_carry ?? base.param_carry
          }
        : base
    }
    setState(built)
    setSaved(built)
  }, [data, defaults])

  const setField = <K extends keyof RowState>(code: string, key: K, v: RowState[K]) =>
    setState((prev) => ({ ...prev, [code]: { ...prev[code], [key]: v } }))

  const showSaved = useShowSavedToast({ editing: true })
  const { trigger, isMutating } = usePromotionKarteUpdateApi(() => {
    showSaved('プロモーションカルテ連携設定')
    mutate()
    mutateHistory()
  })

  const modeRows = PROMOS.filter((p) => p.mode)
  const urlRows = PROMOS.filter((p) => p.url)

  const notifyConnected = state[NOTIFY_API].api_status === CONNECTED
  const targetlistConnected = state[TARGETLIST_API].api_status === CONNECTED

  // 変更検知
  const modeChanged = (code: string) => {
    const a = state[code]
    const b = saved[code]
    return (
      a.api_status !== b.api_status ||
      a.integration_mode !== b.integration_mode ||
      a.notify_mode !== b.notify_mode ||
      a.target_list_mode !== b.target_list_mode ||
      a.enabled !== b.enabled
    )
  }
  const apiChanged = (code: string) => state[code].api_status !== saved[code].api_status
  const urlChanged = (code: string) => {
    const a = state[code]
    const b = saved[code]
    return (
      a.admin_path !== b.admin_path ||
      a.admin_path_v6 !== b.admin_path_v6 ||
      a.param_carry !== b.param_carry
    )
  }

  // 「施策別 連携方式」カード＝施策行 ＋ 共有API予約行
  const modeCodes = modeRows.map((p) => p.code)
  const modeSectionChanged = [
    ...modeCodes.filter(modeChanged),
    ...[NOTIFY_API, TARGETLIST_API].filter(apiChanged)
  ]
  const urlChangedCodes = urlRows.map((p) => p.code).filter(urlChanged)

  const saveMode = () => {
    if (modeSectionChanged.length === 0) return
    const configs: PromotionKarteConfigItem[] = []
    for (const code of modeCodes.filter(modeChanged)) {
      configs.push({
        promotion_type: code,
        api_status: state[code].api_status,
        integration_mode: state[code].integration_mode,
        notify_mode: state[code].notify_mode,
        target_list_mode: state[code].target_list_mode,
        enabled: state[code].enabled
      })
    }
    for (const code of [NOTIFY_API, TARGETLIST_API].filter(apiChanged)) {
      configs.push({ promotion_type: code, api_status: state[code].api_status })
    }
    trigger({ configs })
  }
  const saveUrl = () => {
    if (urlChangedCodes.length === 0) return
    const configs: PromotionKarteConfigItem[] = urlChangedCodes.map((code) => ({
      promotion_type: code,
      admin_path: state[code].admin_path,
      admin_path_v6: state[code].admin_path_v6,
      param_carry: state[code].param_carry
    }))
    trigger({ configs })
  }
  const resetCodes = (codes: string[], fields: (keyof RowState)[]) =>
    setState((prev) => {
      const next = { ...prev }
      for (const code of codes) {
        const patch: Partial<RowState> = {}
        for (const f of fields) (patch as Record<string, unknown>)[f] = saved[code][f]
        next[code] = { ...next[code], ...patch }
      }
      return next
    })
  const discardMode = () => {
    resetCodes(modeCodes, [
      'api_status',
      'integration_mode',
      'notify_mode',
      'target_list_mode',
      'enabled'
    ])
    resetCodes([NOTIFY_API, TARGETLIST_API], ['api_status'])
  }
  const discardUrl = () =>
    resetCodes(
      urlRows.map((p) => p.code),
      ['admin_path', 'admin_path_v6', 'param_carry']
    )

  const history = historyData?.items ?? []

  return (
    <Main type={MainLayoutType.FULL}>
      <PageTitle
        title='プロモーションカルテ連携設定'
        description='施策ごとの W2 Commerce への連携方式と、管理画面誘導用の登録URLを設定します。本設定はプロモーションカルテ（DataInsight）が読み取って実行方式を切り替えるための値であり、管理者が現在の連携状況を把握・管理するための画面です。'
      />
      <Content>
        <div className='bg-surface-emphasis text-emphasis mt-1 rounded-lg px-3 py-2 text-[12px]'>
          W2
          API準備状況が「接続済み」になった施策・機能のみ、連携方式で「API連携」を選択できます。API未接続のうちは「管理画面誘導」で稼働します。
        </div>

        {/* ① 施策別 連携方式 */}
        <Area>
          <Heading>施策別 連携方式</Heading>
          <AreaContent>
            <Text size={12} color={TextColor.SECONDARY}>
              各施策の「W2
              API準備状況」を未提供／提供済み／接続済みで管理します。接続済みにすると、その施策の連携方式で「API連携」が選べます。告知（ポップアップ）とターゲットリストは施策共通のAPIのため、下記で提供状況をまとめて管理します。
            </Text>

            {/* 共有APIの提供状況（表の上でまとめて管理） */}
            <div className='border-primary mt-3 flex flex-wrap items-center gap-x-6 gap-y-3 rounded-lg border px-4 py-3'>
              <span className='text-secondary text-[12px] font-semibold'>共有API 提供状況</span>
              <div className='flex items-center gap-2'>
                <span className='text-[12px]'>告知（ポップアップ）API</span>
                <ApiStatusSelect
                  value={state[NOTIFY_API].api_status}
                  onChange={(v) => setField(NOTIFY_API, 'api_status', v)}
                />
              </div>
              <div className='flex items-center gap-2'>
                <span className='text-[12px]'>ターゲットリスト API</span>
                <ApiStatusSelect
                  value={state[TARGETLIST_API].api_status}
                  onChange={(v) => setField(TARGETLIST_API, 'api_status', v)}
                />
              </div>
              <span className='text-tertiary text-[11px]'>
                ※ 接続済みにすると各列の「API連携」が選択可能になります
              </span>
            </div>

            <div className='mt-3 overflow-x-auto'>
              <table className='w-full border-collapse text-sm'>
                <thead>
                  <tr className='bg-fill-surface-tertiary'>
                    <th className={th}>施策</th>
                    <th className={th}>W2 対象テーブル</th>
                    <th className={th}>W2 API準備状況</th>
                    <th className={th}>施策登録の連携方式</th>
                    <th className={th}>告知（ポップアップ）</th>
                    <th className={th}>ターゲットリスト</th>
                    <th className={th}>有効</th>
                  </tr>
                </thead>
                <tbody>
                  {modeRows.map((p) => {
                    const m = p.mode!
                    const row = state[p.code]
                    const integConnected = row.api_status === CONNECTED
                    return (
                      <tr key={p.code}>
                        <td className={td}>
                          <div className='flex items-center gap-2'>
                            <span
                              className='grid h-7 w-7 flex-none place-items-center rounded-lg text-sm'
                              style={{ background: `${p.iconColor}22`, color: p.iconColor }}
                            >
                              {p.icon}
                            </span>
                            <span>
                              <span className='text-[12.5px] font-semibold'>{p.name}</span>
                              <br />
                              <code className='text-tertiary font-mono text-[10.5px]'>
                                {p.code}
                              </code>
                            </span>
                          </div>
                        </td>
                        <td className={`${td} text-secondary font-mono text-[11px]`}>
                          {m.w2Table.map((t, i) => (
                            <span key={t}>
                              {i > 0 && <br />}
                              {t}
                            </span>
                          ))}
                        </td>
                        <td className={td}>
                          {m.external ? (
                            <span
                              className='rounded-md px-2 py-0.5 text-[10.5px] font-bold'
                              style={apiStatusStyle(EXTERNAL_STATUS)}
                            >
                              {EXTERNAL_STATUS}
                            </span>
                          ) : (
                            <ApiStatusSelect
                              value={row.api_status}
                              onChange={(v) => setField(p.code, 'api_status', v)}
                            />
                          )}
                        </td>
                        <td className={td}>
                          <ModeSelect
                            options={buildOpts(m.integration, AP_INTEGRATION, integConnected)}
                            value={row.integration_mode}
                            onChange={(v) => setField(p.code, 'integration_mode', v)}
                          />
                        </td>
                        <td className={td}>
                          <ModeSelect
                            options={buildOpts(m.notify, AP_NOTIFY, notifyConnected)}
                            value={row.notify_mode}
                            onChange={(v) => setField(p.code, 'notify_mode', v)}
                          />
                        </td>
                        <td className={td}>
                          <ModeSelect
                            options={buildOpts(m.targetlist, AP_TARGETLIST, targetlistConnected)}
                            value={row.target_list_mode}
                            onChange={(v) => setField(p.code, 'target_list_mode', v)}
                          />
                        </td>
                        <td className={`${td} text-center`}>
                          <label className='relative inline-block h-[21px] w-[38px]'>
                            <input
                              type='checkbox'
                              className='peer sr-only'
                              checked={row.enabled}
                              onChange={(e) => setField(p.code, 'enabled', e.target.checked)}
                            />
                            <span className='absolute inset-0 cursor-pointer rounded-full bg-[#CBD3DB] transition peer-checked:bg-[#1A9E6B]' />
                            <span className='absolute top-[3px] left-[3px] h-[15px] w-[15px] rounded-full bg-white transition peer-checked:translate-x-[17px]' />
                          </label>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className='mt-3 flex flex-wrap gap-4 text-[11.5px]'>
              <span className='flex items-center gap-1.5'>
                <i
                  className='inline-block h-2.5 w-2.5 rounded-sm'
                  style={{ background: '#0C6E6A' }}
                />
                API連携：カルテから自動登録
              </span>
              <span className='flex items-center gap-1.5'>
                <i
                  className='inline-block h-2.5 w-2.5 rounded-sm'
                  style={{ background: '#D08A1A' }}
                />
                管理画面誘導：W2画面を開き人が登録
              </span>
              <span className='flex items-center gap-1.5'>
                <i
                  className='inline-block h-2.5 w-2.5 rounded-sm'
                  style={{ background: '#CBD3DB' }}
                />
                手動：指示書を出力し人が作業
              </span>
            </div>

            <div className='mt-4 flex items-center gap-3'>
              <Button
                color={ButtonColor.PRIMARY}
                disabled={modeSectionChanged.length === 0}
                loading={isMutating}
                onClick={saveMode}
              >
                設定を保存
              </Button>
              <Button
                color={ButtonColor.SECONDARY}
                disabled={modeSectionChanged.length === 0}
                onClick={discardMode}
              >
                変更を破棄
              </Button>
              <Text size={12} color={TextColor.SECONDARY}>
                {modeSectionChanged.length > 0
                  ? `${modeSectionChanged.length}件の変更があります`
                  : '変更はありません'}
              </Text>
            </div>
          </AreaContent>
        </Area>

        {/* ② W2 管理画面 登録URL（管理画面誘導用） */}
        <Area>
          <Heading>W2 管理画面 登録URL（管理画面誘導用）</Heading>
          <AreaContent>
            <Text size={12} color={TextColor.SECONDARY}>
              マルチテナントのため、URLは「サイトURL ＋
              登録URL（パス）」で構成されます。ベースURLはサイトごとに解決されるため、ここでは施策別の登録URL（パス部分）のみを登録します。V6
              で Vue 化された画面は V5 とパスが違うため、W2Commerce V5 用と V6
              用を分けて登録します（どちらを使うかはテナントのバージョンに応じてプロモーションカルテ側で決めます）。
            </Text>

            <div className='bg-surface-secondary mt-3 flex flex-wrap items-center gap-3 rounded-lg px-3 py-2 text-[12.5px]'>
              <span className='text-secondary font-semibold'>サイトURL</span>
              <code className='bg-surface text-emphasis rounded px-2 py-1 font-mono text-[12px]'>
                {SITE_URL_TOKEN}
              </code>
              <span className='text-tertiary ml-auto text-[11px]'>
                ※ <code className='font-mono'>{SITE_URL_TOKEN}</code>{' '}
                は利用時に各テナントのサイトURLへ自動解決されます
              </span>
            </div>

            <div className='mt-3 overflow-x-auto'>
              <table className='w-full border-collapse text-sm'>
                <thead>
                  <tr className='bg-fill-surface-tertiary'>
                    <th className={th}>施策</th>
                    <th className={th}>W2 該当機能</th>
                    <th className={th}>登録URL V5（パス）</th>
                    <th className={th}>登録URL V6（パス）</th>
                    <th className={th}>結合後URL（プレビュー）</th>
                    <th className={th}>パラメータ引継ぎ</th>
                    <th className={th}>登録</th>
                  </tr>
                </thead>
                <tbody>
                  {urlRows.map((p) => {
                    const row = state[p.code]
                    const path = row.admin_path.trim()
                    const pathV6 = row.admin_path_v6.trim()
                    const noV5 = !!p.url!.noV5
                    // V5 に機能が無い施策は V6 だけで登録済みとする
                    const registered = (noV5 || path.length > 0) && pathV6.length > 0
                    const partial = !registered && (path.length > 0 || pathV6.length > 0)
                    const withSlash = (v: string) => (v.startsWith('/') ? v : `/${v}`)
                    return (
                      <tr key={p.code}>
                        <td className={`${td} whitespace-nowrap`}>
                          <span className='mr-1'>{p.icon}</span>
                          {p.name}
                        </td>
                        <td className={`${td} text-secondary text-[12px]`}>{p.url!.w2Feature}</td>
                        <td className={td}>
                          {noV5 ? (
                            <span className='text-tertiary text-[11px]'>V5 は該当機能なし</span>
                          ) : (
                            <input
                              className='border-border-input text-emphasis w-full min-w-[170px] rounded-md border px-2 py-1.5 font-mono text-[11.5px]'
                              value={row.admin_path}
                              placeholder={p.url!.pathPlaceholder}
                              onChange={(e) => setField(p.code, 'admin_path', e.target.value)}
                            />
                          )}
                        </td>
                        <td className={td}>
                          <input
                            className='border-border-input text-emphasis w-full min-w-[170px] rounded-md border px-2 py-1.5 font-mono text-[11.5px]'
                            value={row.admin_path_v6}
                            placeholder={p.url!.pathPlaceholderV6 ?? '/xxxx（未登録）'}
                            onChange={(e) => setField(p.code, 'admin_path_v6', e.target.value)}
                          />
                        </td>
                        <td className={td}>
                          {path || pathV6 ? (
                            <div className='flex flex-col gap-1'>
                              {[
                                ['V5', path],
                                ['V6', pathV6]
                              ]
                                .filter(([, v]) => v)
                                .map(([ver, v]) => (
                                  <span
                                    key={ver}
                                    className='block max-w-[260px] font-mono text-[10.5px] break-all'
                                  >
                                    <span className='text-tertiary mr-1'>{ver}</span>
                                    <span className='text-tertiary'>{SITE_URL_TOKEN}</span>
                                    <span className='font-semibold' style={{ color: '#4F46E5' }}>
                                      {withSlash(v)}
                                    </span>
                                  </span>
                                ))}
                            </div>
                          ) : (
                            <span className='text-tertiary text-[10.5px] italic'>
                              登録URL未設定
                            </span>
                          )}
                        </td>
                        <td className={td}>
                          <select
                            className='border-border-input rounded-md border px-2 py-1.5 text-[11.5px]'
                            value={row.param_carry}
                            onChange={(e) => setField(p.code, 'param_carry', e.target.value)}
                          >
                            {p.url!.param.map((o) => (
                              <option key={o} value={o}>
                                {o}
                              </option>
                            ))}
                          </select>
                        </td>
                        <td className={td}>
                          <span
                            className='rounded-md px-2 py-0.5 text-[10.5px] font-bold whitespace-nowrap'
                            style={
                              registered
                                ? { background: '#E5F4ED', color: '#1A9E6B' }
                                : { background: '#FBF1DE', color: '#D08A1A' }
                            }
                          >
                            {registered ? '登録済' : partial ? '一部登録' : '未登録'}
                          </span>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>

            <div className='mt-4 flex items-center gap-3'>
              <Button
                color={ButtonColor.PRIMARY}
                disabled={urlChangedCodes.length === 0}
                loading={isMutating}
                onClick={saveUrl}
              >
                登録URLを保存
              </Button>
              <Button
                color={ButtonColor.SECONDARY}
                disabled={urlChangedCodes.length === 0}
                onClick={discardUrl}
              >
                変更を破棄
              </Button>
              <Text size={12} color={TextColor.SECONDARY}>
                {urlChangedCodes.length > 0
                  ? `${urlChangedCodes.length}件の変更があります`
                  : '変更はありません'}
              </Text>
            </div>

            <div className='bg-surface-emphasis text-emphasis mt-3 rounded-lg px-3 py-2 text-[12px] leading-relaxed'>
              呼び出し時に、各テナントのサイトURL（
              <code className='font-mono'>{SITE_URL_TOKEN}</code>）と{' '}
              <code className='font-mono'>promotion_karte_configs.admin_path</code>（V5
              の登録URL）または <code className='font-mono'>admin_path_v6</code>（V6
              の登録URL）を結合します。サイトURLは利用時に site_id
              ごとに解決されるため、テナントが増えても登録URLの再設定は不要です。
            </div>
          </AreaContent>
        </Area>

        {/* 変更履歴（監査） */}
        <Area>
          <Heading>変更履歴</Heading>
          <AreaContent>
            <Text size={12} color={TextColor.SECONDARY}>
              誰がいつ、どの施策・機能の設定を変更したかを記録します（新しい順）。
            </Text>
            <div className='mt-3 overflow-x-auto'>
              <table className='w-full border-collapse text-sm'>
                <thead>
                  <tr className='bg-fill-surface-tertiary'>
                    <th className={th}>日時</th>
                    <th className={th}>対象</th>
                    <th className={th}>変更項目</th>
                    <th className={th}>変更内容</th>
                    <th className={th}>実行者</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map((h) => (
                    <tr key={h.id}>
                      <td
                        className={`${td} text-secondary font-mono text-[11.5px] whitespace-nowrap`}
                      >
                        {h.created_at ? dateTimeStr(h.created_at) : '—'}
                      </td>
                      <td className={`${td} whitespace-nowrap`}>{nameOf(h.promotion_type)}</td>
                      <td className={`${td} whitespace-nowrap`}>
                        {FIELD_LABEL[h.field] ?? h.field}
                      </td>
                      <td className={td}>
                        <span className='text-tertiary line-through'>
                          {dispVal(h.field, h.before_value)}
                        </span>
                        <span className='mx-1.5'>→</span>
                        <span className='font-semibold' style={{ color: '#1A9E6B' }}>
                          {dispVal(h.field, h.after_value)}
                        </span>
                      </td>
                      <td className={`${td} whitespace-nowrap`}>{h.operator_name ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {history.length === 0 && (
                <div className='py-8 text-center'>
                  <Text color={TextColor.SECONDARY}>変更履歴はまだありません</Text>
                </div>
              )}
            </div>
          </AreaContent>
        </Area>
      </Content>
    </Main>
  )
}

export default PromotionKarteConfigPage
